import { Check, LocalShipping, Store, Widgets } from '@mui/icons-material'
import { breakpoints } from '../../../../theme/breakpoints'
import React from 'react'
import { show as showNiceModal } from '@ebay/nice-modal-react'
import { I18n } from 'common'
import { defaultTheme } from '../../../../theme/index'
import styled from 'styled-components'
import { DdsDetail } from '../../../../store/orderHistory/interfaces'
import TrackingHistory from './drawer/TrackingHistory'

enum Step {
  ORDERED,
  PACKED,
  SHIPPED,
  DELIVERED,
  UNAVAILABLE,
}
interface DdsStepsProps {
  state: number
  mobile?: boolean
  ddsDetail?: DdsDetail
  clickable?: boolean
  shippingId?: string
  shipmentNumber?: number
}

function DDSAdvancedTracker(props: DdsStepsProps): React.ReactElement {
  const {
    state,
    mobile = false,
    ddsDetail,
    shippingId,
    shipmentNumber,
    clickable = true,
  } = props

  const iconColor = (active: boolean, passed: boolean, step: number) => {
    if ((!active && passed) || (active && step === Step.DELIVERED)) {
      return defaultTheme.colors.ddsCompleted
    }
    if (active) {
      return defaultTheme.colors.primary
    }
    return defaultTheme.colors.disabledButtonText
  }
  const renderIcon = (
    active: boolean,
    passed: boolean,
    step: number
  ): React.ReactElement => {
    if (passed) {
      return (
        <Check
          style={{
            color: iconColor(active, passed, step),
          }}
        />
      )
    }
    switch (step) {
      case Step.ORDERED:
        return (
          <Store
            style={{
              color: iconColor(active, passed, step),
            }}
          />
        )
      case Step.PACKED:
        return (
          <Widgets
            style={{
              color: iconColor(active, passed, step),
            }}
          />
        )
      case Step.SHIPPED:
        return (
          <LocalShipping
            style={{
              color: iconColor(active, passed, step),
            }}
          />
        )
      default:
        return (
          <Check
            style={{
              color: iconColor(active, passed, step),
            }}
          />
        )
    }
  }

  const handleTrackingSystemModalMobile = () => {
    showNiceModal(TrackingHistory, {
      ddsDetail: { ...ddsDetail, shippingId, shipmentNumber },
      state,
    })
  }

  const handleTrackingSystemModalDesktop = () => {
    if (state !== Step.UNAVAILABLE) {
      showNiceModal(TrackingHistory, {
        ddsDetail: { ...ddsDetail, shippingId, shipmentNumber },
        state,
      })
    }
  }

  if (mobile) {
    const stateLabels = ['Ordered', 'Packed', 'Shipped', 'Delivered']
    return (
      <MobileStep
        onClick={() => {
          if (clickable) handleTrackingSystemModalMobile()
        }}
      >
        {renderIcon(true, false, state)}
        <I18n name={stateLabels[state]} />
      </MobileStep>
    )
  }
  return (
    <Container
      onClick={() => {
        if (clickable) handleTrackingSystemModalDesktop()
      }}
      ddsClick={clickable}
    >
      <StateItem>
        <Colorindicator
          step={Step.ORDERED}
          passed={state > Step.ORDERED}
          active={state === Step.ORDERED}
          style={{ borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }}
        />

        <DetailsWrapper>
          {renderIcon(
            state === Step.ORDERED,
            state > Step.ORDERED,
            Step.ORDERED
          )}
          <DetailsLabel
            step={Step.ORDERED}
            passed={state > Step.ORDERED}
            active={state === Step.ORDERED}
          >
            <I18n name="ordered" />
          </DetailsLabel>
        </DetailsWrapper>
      </StateItem>

      <StateItem>
        <Colorindicator
          step={Step.PACKED}
          passed={state > Step.PACKED}
          active={state === Step.PACKED}
        />

        <DetailsWrapper>
          {renderIcon(state === Step.PACKED, state > Step.PACKED, Step.PACKED)}

          <DetailsLabel
            step={Step.PACKED}
            passed={state > Step.PACKED}
            active={state === Step.PACKED}
          >
            <I18n name="packed" />
          </DetailsLabel>
        </DetailsWrapper>
      </StateItem>

      <StateItem>
        <Colorindicator
          step={Step.SHIPPED}
          passed={state > Step.SHIPPED}
          active={state === Step.SHIPPED}
        />

        <DetailsWrapper>
          {renderIcon(
            state === Step.SHIPPED,
            state > Step.SHIPPED,
            Step.SHIPPED
          )}
          <DetailsLabel
            step={Step.SHIPPED}
            passed={state > Step.SHIPPED}
            active={state === Step.SHIPPED}
          >
            <I18n name="shipped" />
          </DetailsLabel>
        </DetailsWrapper>
      </StateItem>

      <StateItem style={{ border: 'none' }}>
        <Colorindicator
          step={Step.DELIVERED}
          passed={state > Step.DELIVERED}
          active={state === Step.DELIVERED}
          style={{ borderTopRightRadius: 4, borderBottomRightRadius: 4 }}
        />
        <DetailsWrapper
          style={{
            borderRight: 'none',
          }}
        >
          {renderIcon(
            state === Step.DELIVERED,
            state > Step.DELIVERED,
            Step.DELIVERED
          )}

          <DetailsLabel
            step={Step.DELIVERED}
            passed={state > Step.DELIVERED}
            active={state === Step.DELIVERED}
          >
            <I18n name="delivered" />
          </DetailsLabel>
        </DetailsWrapper>
      </StateItem>
    </Container>
  )
}

export default DDSAdvancedTracker

interface ContainerProps {
  ddsClick: boolean
}

const MobileStep = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px;
  gap: 8px;

  width: 114px;
  height: 32px;

  @media screen and (width <= ${breakpoints.tablet}px) {
    height: 32px;
  }

  /* White */

  background: ${defaultTheme.colors.white};
  border: 1px solid #e5e4e1;
  border-radius: 4px;
`

const Container = styled.div<ContainerProps>`
  width: auto;
  height: 60px;
  background: ${defaultTheme.colors.componentsBackground};
  border: 1px solid ${defaultTheme.colors.borderTable};
  border-radius: 4px;
  padding: 6px 6px 0px 6px;
  display: flex;
  cursor: ${(d) => (!d.ddsClick ? 'not-allowed' : 'pointer')};
  &:hover {
    border: 1px solid #4a4a4a;
  }
  @media print {
    display: none;
  }
`

const StateItem = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  height: 100%;
`
interface ColorIndicatorProps {
  active: boolean
  passed: boolean
  step: number
}
const Colorindicator = styled.div<ColorIndicatorProps>`
  height: 14px;
  width: 100%;
  left: 0px;
  top: 0px;
  border-top: 1px solid ${defaultTheme.colors.greyBorder};
  border-bottom: 1px solid ${defaultTheme.colors.greyBorder};
  border-right: 1px solid ${defaultTheme.colors.greyBorder};
  border-left: ${(p) =>
    p.step === Step.ORDERED
      ? `1px solid ${defaultTheme.colors.greyBorder}`
      : 'none'};
  background: ${(p) => {
    if ((!p.active && p.passed) || (p.active && p.step === Step.DELIVERED)) {
      return `linear-gradient(180deg, ${defaultTheme.colors.ddsCompletedFade} -46.43%, ${defaultTheme.colors.ddsCompleted} 92.86%);`
    }
    if (p.active) {
      return `linear-gradient(180deg, ${defaultTheme.colors.primaryFade} -46.43%, ${defaultTheme.colors.primary} 92.86%);`
    }
    return defaultTheme.colors.componentsBackground
  }};
`
const DetailsWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  height: 100%;
  border-right: 1px solid ${defaultTheme.colors.borderTable};
  padding: 0px 10px 0px 10px;
`
interface DetailsLabelProps {
  active: boolean
  passed: boolean
  step: number
}
const DetailsLabel = styled.span<DetailsLabelProps>`
  font-family: Roboto;
  font-style: normal;
  font-weight: ${(p) => (p.active ? 'bold' : '400')};
  font-size: 14px;
  line-height: 24px;
  text-transform: capitalize;
  color: ${(p) => {
    if (
      (!p.active && p.passed) ||
      (p.active && p.step === Step.DELIVERED) ||
      p.active
    ) {
      return defaultTheme.colors.primaryText
    }

    return defaultTheme.colors.greyDark
  }};
`

interface DescriptionTextProps {
  bold?: boolean
}

export const DDSDescriptionText = styled.span<DescriptionTextProps>`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: ${(p) => (p.bold ? 'bold' : '400')};
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: ${defaultTheme.colors.greyDark};
`
